import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money, dt } from "../../lib/api";
import { Page, btnGold, inputCls, STATUS_FR } from "../../components/store/ui";
import { OrderReviews } from "../../components/store/OrderReviews";

const REASONS = [["defectueux", "Produit défectueux"], ["non_conforme", "Non conforme à la description"], ["non_recu", "Colis non reçu"], ["retractation", "Rétractation"]];

function ReturnForm({ o, onDone }) {
  const [reason, setReason] = useState("defectueux");
  const [comment, setComment] = useState("");
  const [sel, setSel] = useState(() => Object.fromEntries(o.items.map((i) => [i.variant_id, i.qty])));
  const submit = async () => {
    const items = Object.entries(sel).filter(([, q]) => q > 0).map(([variant_id, qty]) => ({ variant_id, qty: Number(qty) }));
    try { const { data } = await api.post("/returns", { order_id: o.id, reason, comment, items }); toast.success(`Demande enregistrée : ${STATUS_FR[data.status] || data.status}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-[#14161D] border border-white/10 p-6 space-y-4" data-testid="return-form">
      <div className="label-xs">Demande de retour / remboursement</div>
      {o.items.map((i) => (
        <div key={i.variant_id} className="flex items-center justify-between text-sm gap-3">
          <span>{i.title} ({i.variant_name})</span>
          <input type="number" min={0} max={i.qty} value={sel[i.variant_id]} data-testid={`return-qty-${i.variant_id}`} onChange={(e) => setSel({ ...sel, [i.variant_id]: e.target.value })} className={`${inputCls} w-20`} />
        </div>
      ))}
      <select data-testid="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls}>{REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <textarea data-testid="return-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (facultatif)" className={inputCls} rows={3} />
      <button data-testid="return-submit" onClick={submit} className={btnGold}>Envoyer la demande</button>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [showReturn, setShowReturn] = useState(false);
  const [mine, setMine] = useState([]);
  const load = () => {
    api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setO(false));
    api.get("/reviews/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (o === false) return <Page title="Commande introuvable" />;
  if (!o) return <div className="p-16 label-xs">Chargement…</div>;
  const canReturn = o.status !== "cancelled" && (o.payment?.status === "paid" || ["paid", "forwarded", "validation_required", "ready_to_order", "supplier_submitted", "supplier_confirmed", "processing", "supplier_problem", "shipped", "delivered", "partially_refunded"].includes(o.status));
  return (
    <Page kicker={dt(o.created_at)} title={o.number}>
      <div className="grid lg:grid-cols-[1fr_360px] gap-12">
        <div className="space-y-10">
          <div>
            <div className="label-xs mb-3">Statut</div>
            <div className="text-xl" data-testid="order-status">{STATUS_FR[o.status] || o.status}</div>
            <div className="text-sm text-muted-foreground mt-1">Livraison estimée au plus tard le {o.delivery_estimate}</div>
          </div>
          <div data-testid="order-tracking">
            <div className="label-xs mb-3">Suivi d'expédition</div>
            {!o.shipments?.length && <p className="text-sm text-muted-foreground">Le numéro de suivi apparaîtra dès sa transmission par le fournisseur.</p>}
            {o.shipments?.map((s) => (
              <div key={s.tracking_number} className="border-l border-[#C9A96E]/40 pl-5 space-y-3">
                <div className="font-mono text-xs">{s.carrier} · {s.tracking_number}</div>
                {s.events.map((e) => <div key={e.code} className="text-sm"><span className="text-[#C9A96E]">●</span> {e.label}</div>)}
              </div>
            ))}
          </div>
          <div>
            <div className="label-xs mb-3">Historique</div>
            {o.events.map((e, i) => <div key={i} className="text-sm py-1.5 border-b border-white/5 flex gap-4"><span className="font-mono text-xs text-muted-foreground w-28 shrink-0">{dt(e.at)}</span>{e.message}</div>)}
          </div>
          <OrderReviews order={o} mine={mine} reload={load} />
          {canReturn && (showReturn ? <ReturnForm o={o} onDone={() => { setShowReturn(false); load(); }} /> : <button data-testid="open-return-form" onClick={() => setShowReturn(true)} className={btnGold}>Demander un retour</button>)}
          {canReturn && <p className="text-xs text-muted-foreground" data-testid="return-email-hint">Vous pouvez aussi demander un retour par courriel à <a href="mailto:boutique.abri@gmail.com" className="underline">boutique.abri@gmail.com</a> avec votre numéro de commande.</p>}
        </div>
        <aside className="bg-[#14161D] border border-white/10 p-8 h-fit text-sm space-y-3">
          {o.items.map((i) => <div key={i.variant_id} className="flex justify-between gap-3"><span>{i.qty} × {i.title}</span><span className="font-mono">{money(i.unit_price_local * i.qty, o.currency)}</span></div>)}
          <div className="flex justify-between border-t border-white/10 pt-3"><span>Total payé</span><span className="font-mono" data-testid="order-total">{money(o.total, o.currency)}</span></div>
          {(o.taxes || []).map((x) => <div key={x.name} className="flex justify-between text-muted-foreground"><span>dont {x.name}</span><span className="font-mono">{money(x.amount, o.currency)}</span></div>)}
          {o.refunded_amount > 0 && <div className="flex justify-between text-[#10B981]"><span>Remboursé</span><span className="font-mono" data-testid="order-refunded">{money(o.refunded_amount, o.currency)}</span></div>}
          <div className="text-xs text-muted-foreground pt-3">{o.address.first_name ? `${o.address.first_name} ${o.address.last_name}` : o.address.name}, {o.address.line1}{o.address.line2 ? `, ${o.address.line2}` : ""}, {o.address.city} ({o.address.province}) {o.address.postal_code}, Canada</div>
        </aside>
      </div>
    </Page>
  );
}
